import { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — counts up from 0 → target when scrolled into view.
 *
 * Uses IntersectionObserver so it fires once as the number enters the
 * viewport (like Stripe / Airbnb landing pages).
 */
export default function AnimatedNumber({
  value,
  duration = 1000,
  suffix = "",
  prefix = "",
  fallback = "—",
  className = "",
  testId,
}) {
  const [display, setDisplay] = useState(0);
  const nodeRef = useRef(null);
  const hasRunRef = useRef(false);

  // Non-numeric or null → show fallback (e.g. "—")
  const target = typeof value === "number" && Number.isFinite(value) ? value : null;

  useEffect(() => {
    if (target === null) return;
    const el = nodeRef.current;
    if (!el) return;

    let cancelled = false;
    let rafId = null;

    const start = () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      const startedAt = performance.now();
      const from = 0;
      const step = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - startedAt) / duration);
        // ease-out cubic — starts fast, coasts to target
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(from + (target - from) * eased));
        if (t < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };

    // If reduced-motion is preferred, jump straight to target.
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setDisplay(target);
      hasRunRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            start();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [target, duration]);

  return (
    <span ref={nodeRef} className={className} data-testid={testId}>
      {target === null ? fallback : `${prefix}${display.toLocaleString()}${suffix}`}
    </span>
  );
}
